import { useId, useState } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import moment from "moment";

import { Text } from "../../components";
import ProgressStepper from "../../components/ProgressStepper";
import { LandToken } from "../../types/LandToken";
import { EcoTokenStatus, onReassignment } from "../../utils/constants";

// Role banner at the top of the LandPage detail column ("You are the Guardian of this
// EcoFutures Token"), now a transitions.dev "Accordion expand": the banner row is the header,
// and expanding it shows the land's verification progress, a stepper plus an info box.
// Figma: https://www.figma.com/design/krf1FusCiqM8PARu6iqPzw/EcoFutures-UI-development--Copy-?node-id=3001-4238
// The Figma puts both on a white card with a grey info box. Here they sit directly on the
// banner's blue, the stepper's own colour, so the info box goes white instead.

const INTER =
  "'Inter',-apple-system,BlinkMacSystemFont,sans-serif,'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol'";
// index.html registers the Plex Mono files as 'IBM Mono'.
const MONO = "'IBM Mono', 'IBM Plex Mono', monospace";

const STEPS = ["Upload", "Claimed", "Verified", "Minted"];
// MUI marks every step below activeStep completed, so one past the last step reads as "all done".
const ALL_DONE = STEPS.length;

type VerificationProgress = {
  activeStep: number;
  errorStep?: number;
  message: string;
  actorLabel?: string;
  actorAddress?: string | null;
  at?: string | Date | null;
};

// The furthest step the timestamps prove was reached. Only for states the status alone can't
// place on the stepper (blocked / cancelled before mint).
const stepFromTimestamps = (timestamps: LandToken["timestamps"]) =>
  timestamps?.verifiedAt ? 3 : timestamps?.claimedAt ? 2 : timestamps?.requestedAt ? 1 : 0;

// Maps the eco-token pipeline onto the four steps:
//   draft → uploaded / verification_required → claimed / verification_prepared → approved → minted
// The timestamps are the backend's et_timestamps row (requestedAt / claimedAt / verifiedAt /
// mintedAt). Older tokens can have gaps there, so every time is optional.
const resolveVerificationProgress = (token: LandToken, own: boolean): VerificationProgress => {
  const subject = own ? "Your land" : "This land";
  const { status, timestamps } = token;
  const verifier = token.trust?.address ?? token.acceptedBy ?? null;

  // Pre-mint tokens have etNft = null. The status fallbacks cover the gap before the mint
  // event has been indexed.
  const minted =
    (token.etNft?.id !== undefined && token.etNft?.id !== null) ||
    status === EcoTokenStatus.VERIFIED ||
    status === EcoTokenStatus.LISTED_TO_SALE;

  if (minted) {
    const mintedBy = {
      activeStep: ALL_DONE,
      actorLabel: "Minted by",
      actorAddress: token.etNft?.minterAddress ?? token.minterAddress,
      at: timestamps?.mintedAt ?? token.etNft?.createdAt,
    };
    if (status === EcoTokenStatus.RE_VERIFICATION_REQUIRED) {
      return { ...mintedBy, message: `${subject} is due for re-verification.` };
    }
    if (onReassignment(status)) {
      return { ...mintedBy, message: `${subject} is being reassigned to a new Verifier.` };
    }
    if (status === EcoTokenStatus.BLOCKED) {
      return { ...mintedBy, message: `${subject} has been blocked.` };
    }
    if (status === EcoTokenStatus.INVALIDATED) {
      return { ...mintedBy, message: `${subject} has been cancelled.` };
    }
    return { ...mintedBy, message: `${subject} has been verified and minted as an EcoFutures Token.` };
  }

  // Pre-mint challenge: approveAndMint reverts while it's open, so it outranks the plain
  // verification copy.
  if (
    token.challengedAt &&
    [EcoTokenStatus.CLAIMED, EcoTokenStatus.VERIFICATION_PREPARED, EcoTokenStatus.APPROVED].includes(status)
  ) {
    return {
      activeStep: status === EcoTokenStatus.APPROVED ? 3 : 2,
      message: `${subject}'s verification is under challenge. Minting is paused until it's resolved.`,
      actorLabel: "Challenged by",
      actorAddress: token.challengedBy,
      at: token.challengedAt,
    };
  }

  const rejected: VerificationProgress = {
    activeStep: 2,
    errorStep: 2,
    message: `${subject} didn't pass verification.`,
    actorLabel: "Rejected by",
    actorAddress: verifier,
  };

  switch (status) {
    case EcoTokenStatus.DRAFT:
      // A draft with a verifier attached is one the verifier sent back, the same "Rejected"
      // that GuardianPortal shows.
      if (token.trust) {
        return rejected;
      }
      return {
        activeStep: 0,
        message: `${subject} is saved as a draft and hasn't been submitted for verification yet.`,
        actorLabel: "Created by",
        actorAddress: token.minterAddress,
        at: timestamps?.createdAt ?? token.createdAt,
      };

    case EcoTokenStatus.UPLOADED:
    case EcoTokenStatus.VERIFICATION_REQUIRED:
      return {
        activeStep: 1,
        message: `${subject} has been uploaded and is waiting for a Verifier to claim it.`,
        actorLabel: "Uploaded by",
        actorAddress: token.minterAddress,
        at: timestamps?.requestedAt ?? timestamps?.createdAt ?? token.createdAt,
      };

    case EcoTokenStatus.REASSIGNMENT_REQUIRED:
    case EcoTokenStatus.REASSIGNMENT_PENDING:
    case EcoTokenStatus.REASSIGNMENT_APPROVED:
      return {
        activeStep: 1,
        message: `${subject} is being reassigned to a new Verifier.`,
        actorLabel: "Uploaded by",
        actorAddress: token.minterAddress,
        at: timestamps?.requestedAt ?? timestamps?.createdAt ?? token.createdAt,
      };

    case EcoTokenStatus.CLAIMED:
    case EcoTokenStatus.VERIFICATION_PREPARED:
    case EcoTokenStatus.RE_VERIFICATION_REQUIRED:
      return {
        activeStep: 2,
        message: `${subject} has been claimed and is pending verification.`,
        actorLabel: "Claimed by",
        actorAddress: verifier,
        at: timestamps?.claimedAt,
      };

    case EcoTokenStatus.APPROVED:
      // The backend only allows minting 7 days after approval, so this says "awaiting",
      // not "ready".
      return {
        activeStep: 3,
        message: `${subject} has been verified and is awaiting minting.`,
        actorLabel: "Verified by",
        actorAddress: verifier,
        at: timestamps?.verifiedAt ?? token.lastVerified,
      };

    case EcoTokenStatus.REJECTED:
    case EcoTokenStatus.VERIFICATION_FAILED:
      return rejected;

    case EcoTokenStatus.BLOCKED:
    case EcoTokenStatus.INVALIDATED: {
      const step = stepFromTimestamps(timestamps);
      return {
        activeStep: step,
        errorStep: step,
        message: `${subject} has been ${status === EcoTokenStatus.BLOCKED ? "blocked" : "cancelled"}.`,
      };
    }

    default:
      return { activeStep: stepFromTimestamps(timestamps), message: `${subject} is in verification.` };
  }
};

const shortAddress = (address: string) => `${address.slice(0, 6)}…${address.slice(-4)}`;

// transitions.dev "Accordion expand" (skill ref 21). The --acc-* tunables live on :root in
// App.tsx. Two changes from the stock snippet, both for this repo's motion rules:
//  - The panel height animates grid-template-rows 0fr → 1fr. That's a layout animation, which
//    CLAUDE.md otherwise rules out. It's allowed here because it's small, one-shot and user
//    initiated (250ms, only on click), and an in-flow panel can't grow without reflowing what's
//    below it. The alternative is FLIP-ing every card underneath, and those cards already run
//    their own IntersectionObserver reveal transforms.
//  - The inner fade uses opacity + translate3d instead of the snippet's filter: blur(2px),
//    which would repaint the whole panel every frame.
// visibility follows the collapse clock, so a closed panel is gone from the a11y tree and tab
// order but stays visible while it collapses.
const accordionSx = {
  "& .t-acc-panel": {
    display: "grid",
    gridTemplateRows: "0fr",
    transition: "grid-template-rows var(--acc-collapse) var(--acc-ease)",
  },
  '&[data-open="true"] .t-acc-panel': {
    gridTemplateRows: "1fr",
    transition: "grid-template-rows var(--acc-expand) var(--acc-ease)",
  },
  // Keep padding off this grid item (spacing lives on its child). Padding on the 0fr track
  // leaves a strip that never collapses.
  "& .t-acc-panel-inner": {
    minHeight: 0,
    overflow: "hidden",
    opacity: 0,
    transform: "translate3d(0, -4px, 0)",
    visibility: "hidden",
    transition:
      "opacity var(--acc-collapse) var(--acc-ease), transform var(--acc-collapse) var(--acc-ease), visibility 0s linear var(--acc-collapse)",
  },
  '&[data-open="true"] .t-acc-panel-inner': {
    opacity: 1,
    transform: "translate3d(0, 0, 0)",
    visibility: "visible",
    transition:
      "opacity var(--acc-expand) var(--acc-ease), transform var(--acc-expand) var(--acc-ease), visibility 0s linear 0s",
  },
  // scaleY(-1) flips the "v" into a "^" through a flat line at the midpoint. It works in every
  // browser, unlike morphing the path's `d`, which only animates in Chromium.
  "& .t-acc-chevron": {
    display: "inline-flex",
    transform: "scaleY(1)",
    transformOrigin: "center",
    transition: "transform var(--acc-chevron) var(--acc-ease)",
  },
  "& .t-acc-chevron path": { vectorEffect: "non-scaling-stroke" },
  '&[data-open="true"] .t-acc-chevron': { transform: "scaleY(-1)" },
  "@media (prefers-reduced-motion: reduce)": {
    "& .t-acc-panel, & .t-acc-panel-inner, & .t-acc-chevron, & .t-acc-chevron-bubble": {
      transition: "none !important",
    },
  },
} as const;

type RoleBannerProps = {
  token?: LandToken;
  landGuardian: boolean;
  landOwner: boolean;
  landTrust: boolean;
  isAdmin: boolean;
  // Breakpoint visibility comes from the call site. LandPage mounts one copy above both columns
  // on mobile and one at the top of the right column on desktop.
  sx?: SxProps<Theme>;
};

const RoleBanner = ({ token, landGuardian, landOwner, landTrust, isAdmin, sx }: RoleBannerProps) => {
  // Open state stays local on purpose: lifting it into LandPage would re-render the whole
  // 7k-line page on every toggle.
  const [open, setOpen] = useState(false);
  const headerId = useId();
  const panelId = useId();

  const progress = token ? resolveVerificationProgress(token, landGuardian || landOwner) : null;

  return (
    <Box
      data-detail-keep
      className="t-acc"
      data-open={open}
      sx={[
        {
          p: { xs: "18px", md: "24px" },
          width: "100%",
          backgroundColor: "#d1efff",
          borderRadius: "20px",
          flexDirection: "column",
        },
        accordionSx,
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        component="button"
        type="button"
        id={headerId}
        className="t-acc-head"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        sx={{
          all: "unset",
          boxSizing: "border-box",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: { xs: "12px", md: "16px" },
          cursor: "pointer",
          borderRadius: "12px",
          "&:focus-visible": { outline: "2px solid #000", outlineOffset: "4px" },
          "&:hover .t-acc-chevron-bubble": { backgroundColor: "#fff" },
        }}
      >
        <Box sx={{ width: "fit-content", flexShrink: 0, display: "flex" }}>
          <InfoIcon sx={{ fontSize: 44, color: "#000" }} />
        </Box>
        <Text
          sx={{
            flex: 1,
            minWidth: 0,
            color: "#000",
            textAlign: "start",
            fontSize: "14px",
            fontWeight: 400,
            fontFamily: INTER,
            lineHeight: "19.2px",
            textTransform: "inherit",
          }}
        >
          {landGuardian
            ? "You are the Guardian of this EcoFutures Token"
            : landOwner
            ? "You are the Owner of this EcoFutures Token"
            : landTrust
            ? "You are the Trustee of this EcoFutures Token"
            : isAdmin
            ? "Admin cannot make offer"
            : "Trust cannot make offer"}
        </Text>
        <Box
          component="span"
          className="t-acc-chevron-bubble"
          sx={{
            flexShrink: 0,
            width: 32,
            height: 32,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            transition: "background-color 150ms ease-out",
          }}
        >
          <span className="t-acc-chevron">
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden focusable="false">
              <path
                d="M4 6.5L8 10.5L12 6.5"
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </Box>
      </Box>

      <div className="t-acc-panel">
        <div className="t-acc-panel-inner" id={panelId} role="region" aria-labelledby={headerId}>
          {progress && (
            <Box sx={{ pt: { xs: "18px", md: "24px" }, display: "flex", flexDirection: "column" }}>
              {/* <Box
                sx={{
                  fontFamily: INTER,
                  fontWeight: 700,
                  fontSize: "12.5px",
                  lineHeight: "16.8px",
                  textTransform: "uppercase",
                  color: "#000",
                }}
              >
                Verification
              </Box> */}

              {/* The banner already is the stepper's blue, so drop its own fill and inset. */}
              <ProgressStepper
                steps={STEPS}
                activeStep={progress.activeStep}
                errorStep={progress.errorStep}
                sx={{ backgroundColor: "transparent", padding: { xs: "16px 0 18px", sm: "16px 0 20px" } }}
              />

              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "20px",
                  p: "16px",
                  pr: { xs: "16px", sm: "24px" },
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: "16px", sm: "24px" },
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 52,
                    height: 52,
                    borderRadius: "26px",
                    backgroundColor: "#d1efff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InfoIcon sx={{ fontSize: 44, color: "#000" }} />
                </Box>
                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
                  <Box
                    sx={{
                      fontFamily: INTER,
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: "22.4px",
                      color: "#000",
                    }}
                  >
                    {progress.message}
                  </Box>
                  {(progress.actorAddress || progress.at) && (
                    <Box
                      sx={{
                        fontFamily: MONO,
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "15.6px",
                        textTransform: "uppercase",
                        color: "#888",
                        display: "flex",
                        flexWrap: "wrap",
                        columnGap: "20px",
                        rowGap: "2px",
                      }}
                    >
                      {progress.actorAddress && progress.actorLabel && (
                        <span>
                          {progress.actorLabel} {/* Checksum case carries meaning, and uppercasing would print "0X". */}
                          <Box component="span" sx={{ color: "#000", textTransform: "none" }}>
                            {shortAddress(progress.actorAddress)}
                          </Box>
                        </span>
                      )}
                      {progress.at && (
                        <span>
                          Time{" "}
                          <Box component="span" sx={{ color: "#000" }}>
                            {moment(progress.at).fromNow()}
                          </Box>
                        </span>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </div>
      </div>
    </Box>
  );
};

export default RoleBanner;
